/**
 * Attach to a scrollable element to cause scrolling to stay "snapped" to the
 * bottom, even when the element's contents change, until the user scrolls up.
 *
 * Values:
 *   startSnapped: If `true`, scrolling is considered to be snapped to the
 *                 bottom at page load.
 *
 * Targets:
 *   observable: If provided, the mutation observer will watch this element.
 *               Multiple observableTargets are supported. If none is provided,
 *               the mutation observer will watch the controller element.
 *
 * Observer currently only watches for child list changes, but watches the whole
 * subtree of the observed element(s).
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    startSnapped: Boolean,
  };

  static targets = ["observable"];

  connect() {
    this.isSnapping = this.startSnappedValue;
    const observerTargets = this.hasObservableTarget ? this.observableTargets : [this.element];

    this.boundHandleScroll = this.handleScroll.bind(this);
    this.element.addEventListener("scroll", this.boundHandleScroll);

    this.observer = new MutationObserver(this.handleMutation.bind(this));
    observerTargets.forEach((target) => {
      this.observer.observe(target, { subtree: true, childList: true });
    });
  }

  disconnect() {
    this.observer.disconnect();
    this.element.removeEventListener("scroll", this.boundHandleScroll);
  }

  handleScroll(e) {
    this.isSnapping = this.bottomDistance() < 1;
  }

  handleMutation(mutationList, observer) {
    this.update();
  }

  update() {
    if (this.isSnapping) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    this.element.scroll({
      top: this.element.scrollHeight - this.element.clientHeight,
    });
  }

  bottomDistance() {
    return Math.abs(this.element.scrollHeight - this.element.clientHeight - this.element.scrollTop);
  }
}
